import { useCallback, useEffect, useMemo, useState } from 'react'
import { Play, Square, Trash2, Plus, RefreshCw, DollarSign, Server, Database, AlertTriangle, CheckCircle, Clock, Zap, Pause, X, Settings, ShieldCheck, Bell, Globe, RotateCcw, History, ListChecks } from 'lucide-react'
import { createMockApiClient, DEMO_API_LABEL, DEMO_MODE } from './mockApi'

const DEFAULT_API_BASE_URL = DEMO_MODE
  ? DEMO_API_LABEL
  : import.meta.env?.VITE_API_BASE_URL ?? 'http://localhost:8000'
const DEFAULT_API_TOKEN = DEMO_MODE ? '' : import.meta.env?.VITE_API_TOKEN ?? ''
const API_CONFIG_STORAGE_KEY = 'comfyui.apiConfig'
const DEFAULT_AUTOSAVE_CONFIG = {
  cadence_minutes: 60,
  categories: ['workflows']
}
const RECIPE_ASSIGNMENTS_STORAGE_KEY = 'comfyui.recipeAssignments'
const DEFAULT_RESTORE_STATE = {
  recipeId: '',
  snapshotPrefix: '',
  categories: [],
  targetPath: '',
  dryRun: false,
  cleanSync: true,
  notes: ''
}

const PROFILE_FORM_TEMPLATE = {
  name: '',
  cadence_minutes: 60,
  categories: 'workflows',
  s3_prefix: '',
  retention_days: '',
  notes: ''
}

const CATEGORY_OPTIONS = [
  { id: 'workflows', label: 'Workflows (prompts, graphs)' },
  { id: 'models', label: 'Models & LoRAs' },
  { id: 'outputs', label: 'Outputs (images/video)' }
]

const createApiClient = (config) => {
  const normalizedBase = (config.apiBaseUrl || DEFAULT_API_BASE_URL).replace(/\/$/, '')
  const authHeader = config.apiToken ? { Authorization: `Bearer ${config.apiToken}` } : {}

  const request = async (endpoint, options = {}) => {
    const response = await fetch(`${normalizedBase}${endpoint}`, {
      headers: {
        'Content-Type': 'application/json',
        ...authHeader,
        ...(options.headers || {})
      },
      ...options
    })

    if (!response.ok) {
      let serverMessage = `HTTP ${response.status}`
      try {
        const body = await response.json()
        serverMessage =
          body?.detail?.message ||
          body?.detail ||
          body?.message ||
          serverMessage
      } catch (err) {
        // ignore JSON parse errors
      }
      throw new Error(serverMessage)
    }

    return response.json()
  }

  return {
    get: (endpoint) => request(endpoint),
    post: (endpoint, data = {}) =>
      request(endpoint, {
        method: 'POST',
        body: JSON.stringify(data)
      }),
    put: (endpoint, data = {}) =>
      request(endpoint, {
        method: 'PUT',
        body: JSON.stringify(data)
      }),
    delete: (endpoint) =>
      request(endpoint, {
        method: 'DELETE'
      })
  }
}

// Enhanced status badge component with proper RunPod status mapping
const StatusBadge = ({ status }) => {
  const statusConfig = {
    'RUNNING': { class: 'status-running', icon: CheckCircle, text: 'Running' },
    'EXITED': { class: 'status-stopped', icon: Pause, text: 'Stopped (Exited)' },
    'STOPPED': { class: 'status-stopped', icon: Clock, text: 'Stopped' },
    'TERMINATED': { class: 'status-terminated', icon: X, text: 'Terminated' },
    'STARTING': { class: 'status-warning', icon: RefreshCw, text: 'Starting' },
    'STOPPING': { class: 'status-warning', icon: RefreshCw, text: 'Stopping' },
    'CREATED': { class: 'status-warning', icon: Clock, text: 'Created' },
    'INITIALIZING': { class: 'status-warning', icon: RefreshCw, text: 'Provisioning' },
    'ERROR': { class: 'status-error', icon: AlertTriangle, text: 'Error' }
  }
  
  const config = statusConfig[status] || statusConfig['ERROR']
  const IconComponent = config.icon
  
  return (
    <span className={config.class}>
      <IconComponent className="w-3 h-3 mr-1" />
      {config.text}
    </span>
  )
}

// Loading spinner component
const LoadingSpinner = ({ size = 'sm' }) => {
  const sizeClass = size === 'lg' ? 'w-8 h-8' : 'w-4 h-4'
  return (
    <RefreshCw className={`${sizeClass} animate-spin text-blue-500`} />
  )
}

const ToastCenter = ({ toasts, onDismiss }) => {
  if (!toasts.length) return null

  return (
    <div className="fixed top-5 right-5 z-40 space-y-3">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={`flex items-start gap-3 rounded-lg px-4 py-3 shadow-lg text-sm text-white ${
            toast.type === 'error'
              ? 'bg-red-600'
              : toast.type === 'success'
                ? 'bg-green-600'
                : 'bg-blue-600'
          }`}
        >
          <Bell className="w-4 h-4 mt-0.5" />
          <div className="flex-1">{toast.message}</div>
          <button onClick={() => onDismiss(toast.id)} className="text-white/80 hover:text-white">
            ×
          </button>
        </div>
      ))}
    </div>
  )
}

const SettingsModal = ({ isOpen, config, onClose, onSave }) => {
  const [formState, setFormState] = useState(config)

  useEffect(() => {
    setFormState(config)
  }, [config])

  if (!isOpen) return null

  const handleSubmit = (event) => {
    event.preventDefault()
    onSave(formState)
  }

  return (
    <div className="modal-overlay">
      <div className="modal-content max-w-xl">
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <Settings className="w-5 h-5" />
          API & Auth Settings
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">API Base URL</label>
            <input
              type="url"
              className="input-field"
              value={formState.apiBaseUrl}
              onChange={(e) => setFormState((prev) => ({ ...prev, apiBaseUrl: e.target.value }))}
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Defaults to <code>VITE_API_BASE_URL</code> or <code>http://localhost:8000</code>
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">API Token (optional)</label>
            <input
              type="password"
              className="input-field"
              value={formState.apiToken}
              onChange={(e) => setFormState((prev) => ({ ...prev, apiToken: e.target.value }))}
              placeholder="Bearer token for authenticated requests"
            />
          </div>
          <div className="flex justify-end gap-3">
            <button type="button" className="btn-secondary btn-sm" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-primary btn-sm">
              Save Settings
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

const TemplateWizardModal = ({
  isOpen,
  onClose,
  data,
  onRefresh,
  isLoading,
  notify
}) => {
  if (!isOpen) return null

  const pods = data?.pods || []
  const bootstrapCommand = data?.bootstrap_command || ''

  const handleCopy = (value, label) => {
    if (!value || !navigator?.clipboard) return
    navigator.clipboard
      .writeText(value)
      .then(() => notify?.('success', `${label} copied to clipboard`))
      .catch(() => notify?.('error', `Unable to copy ${label}`))
  }

  const statusBadge = (status) => {
    const map = {
      ready: { text: 'Ready', className: 'bg-green-100 text-green-700' },
      missing_ssh: { text: 'Missing SSH', className: 'bg-yellow-100 text-yellow-700' },
      pending_ports: { text: 'Pending Ports', className: 'bg-blue-100 text-blue-700' },
      not_running: { text: 'Not Running', className: 'bg-gray-100 text-gray-700' }
    }
    const config = map[status] || map.missing_ssh
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${config.className}`}>
        {config.text}
      </span>
    )
  }

  return (
    <div className="modal-overlay">
      <div className="modal-content max-w-4xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">Template Wizard & SSH Readiness</h2>
          <div className="flex gap-2">
            <button className="btn-secondary btn-sm" onClick={onClose}>
              Close
            </button>
            <button className="btn-primary btn-sm" onClick={onRefresh} disabled={isLoading}>
              {isLoading ? <LoadingSpinner /> : 'Re-check pods'}
            </button>
          </div>
        </div>
        <div className="space-y-6">
          <div className="rounded-lg border border-gray-200 p-4 bg-gray-50">
            <h3 className="text-sm font-semibold text-gray-800 mb-2">How this works</h3>
            <ol className="list-decimal list-inside text-sm text-gray-700 space-y-1">
              <li>Add your automation SSH key in RunPod (Settings → SSH Keys). Dashboard detects it automatically.</li>
              <li>Update your template’s command with the bootstrap snippet below so OpenSSH starts automatically.</li>
              <li>Ensure the template exposes <code>22/tcp</code>. Once pods run the template, the table will show “Ready”.</li>
            </ol>
            <p className="text-xs text-gray-500 mt-2">
              The wizard never edits templates directly; it simply provides the exact snippet and a live readiness report.
            </p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <div>
                <h3 className="text-sm font-semibold text-gray-800">OpenSSH bootstrap snippet</h3>
                <p className="text-xs text-gray-500">
                  Paste this into the RunPod template command (replace <code>sleep infinity</code> or append before it).
                </p>
              </div>
              <button
                className="btn-secondary btn-xs"
                onClick={() => handleCopy(bootstrapCommand, 'Bootstrap snippet')}
              >
                Copy
              </button>
            </div>
            <pre className="bg-gray-900 text-gray-100 text-xs p-3 rounded-lg overflow-x-auto">
{bootstrapCommand}
            </pre>
          </div>

          <div className="rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-sm font-semibold text-gray-800">Pod SSH readiness</h3>
                <p className="text-xs text-gray-500">
                  We look for published 22/tcp ports. Pods must be RUNNING for RunPod to expose the connection info.
                </p>
              </div>
              <button className="btn-secondary btn-xs" onClick={onRefresh} disabled={isLoading}>
                {isLoading ? 'Checking…' : 'Refresh'}
              </button>
            </div>
            {isLoading ? (
              <div className="flex items-center justify-center py-6">
                <LoadingSpinner size="lg" />
              </div>
            ) : pods.length === 0 ? (
              <p className="text-sm text-gray-600">No pods found yet. Create a pod to verify SSH readiness.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                      <th className="py-2 pr-3">Pod</th>
                      <th className="py-2 pr-3">SSH</th>
                      <th className="py-2 pr-3">ComfyUI</th>
                      <th className="py-2 pr-3">Message</th>
                      <th className="py-2">SSH Command</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pods.map((pod) => (
                      <tr key={pod.pod_id} className="border-t border-gray-100">
                        <td className="py-2 pr-3">
                          <div className="font-semibold text-gray-800">{pod.pod_name}</div>
                          <div className="text-xs text-gray-500">{pod.pod_id}</div>
                        </td>
                        <td className="py-2 pr-3">
                          <div className="flex flex-col">
                            {statusBadge(pod.status)}
                            <span className="text-[0.65rem] text-gray-500">
                              {pod.endpoint
                                ? `${pod.endpoint.host}:${pod.endpoint.port}`
                                : 'No SSH'}
                            </span>
                          </div>
                        </td>
                        <td className="py-2 pr-3 text-xs text-gray-600">
                          {pod.http_endpoint ? (
                            <button
                              className="text-blue-600 hover:underline"
                              onClick={() =>
                                window.open(
                                  pod.http_endpoint.proxy_url || pod.http_endpoint.url,
                                  '_blank',
                                  'noopener'
                                )
                              }
                            >
                              {pod.http_endpoint.proxy_url || pod.http_endpoint.url}
                            </button>
                          ) : (
                            'No HTTP'
                          )}
                        </td>
                        <td className="py-2 pr-3 text-xs text-gray-500">{pod.message}</td>
                        <td className="py-2 text-xs text-gray-600">
                          {pod.ssh_command ? (
                            <button
                              className="text-blue-600 hover:underline"
                              onClick={() => handleCopy(pod.ssh_command, 'SSH command')}
                            >
                              {pod.ssh_command}
                            </button>
                          ) : (
                            '—'
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="rounded-lg border border-gray-200 p-4 bg-gray-50 text-xs text-gray-600">
            <p>
              Need a refresher? See the full SSH template checklist in <code>docs/runbooks/runpod_operations.md</code>.
              Once pods show “Ready”, restores and automations use the provided SSH key automatically—no further action needed.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

const SkeletonCard = () => (
  <div className="card p-6 animate-pulse">
    <div className="h-4 bg-gray-200 rounded w-1/3 mb-4" />
    <div className="h-3 bg-gray-200 rounded w-2/3 mb-2" />
    <div className="h-3 bg-gray-200 rounded w-1/2 mb-6" />
    <div className="grid grid-cols-2 gap-4">
      <div className="h-10 bg-gray-200 rounded" />
      <div className="h-10 bg-gray-200 rounded" />
    </div>
  </div>
)

const normalizeStatus = (status) => {
  if (!status) return 'unknown'
  if (status === 'configured') return 'configured'
  if (status === 'unconfigured') return 'unconfigured'
  if (typeof status === 'string' && status.toLowerCase().startsWith('error')) return 'error'
  return 'unknown'
}

const HealthBadge = ({ label, status }) => {
  const normalized = normalizeStatus(status)
  const statusMap = {
    configured: { text: 'Configured', color: 'text-green-600 bg-green-50' },
    unconfigured: { text: 'Not Configured', color: 'text-yellow-600 bg-yellow-50' },
    error: { text: 'Error', color: 'text-red-600 bg-red-50' },
    default: { text: 'Unknown', color: 'text-gray-600 bg-gray-50' }
  }
  const state = statusMap[normalized] || statusMap.default
  return (
    <span className={`inline-flex items-center gap-2 text-xs font-medium px-3 py-1 rounded-full ${state.color}`}>
      <ShieldCheck className="w-3 h-3" />
      {label}: {state.text}
    </span>
  )
}

// Enhanced Pod creation modal with live GPU data
const STORAGE_MONTHLY_USD_PER_GB = 0.07 // https://docs.runpod.io/storage/network-volumes/pricing
const STORAGE_HOURLY_PER_GB = STORAGE_MONTHLY_USD_PER_GB / (30 * 24)

const CreatePodModal = ({ isOpen, onClose, onCreatePod, gpuTypes = [], inline = false, onInlineClose }) => {
  const [formData, setFormData] = useState({
    gpu_type_id: '',
    pod_name: 'comfyui-pod',
    pod_type: 'ON_DEMAND',
    cloud_type: 'COMMUNITY',
    gpu_count: 1,
    container_disk_gb: 50,
    volume_gb: 20,
  })
  const [customBidEnabled, setCustomBidEnabled] = useState(false)
  const [customBidValue, setCustomBidValue] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState(null)

  const resetForm = () => {
    setFormData({
      gpu_type_id: '',
      pod_name: 'comfyui-pod',
      pod_type: 'ON_DEMAND',
      cloud_type: 'COMMUNITY',
      gpu_count: 1,
      container_disk_gb: 50,
      volume_gb: 20
    })
    setCustomBidEnabled(false)
    setCustomBidValue('')
  }

  const handleClear = () => {
    if (isSubmitting) return
    resetForm()
    setSubmitError(null)
  }

  const handleCancel = () => {
    if (isSubmitting) return
    if (inline) {
      setSubmitError(null)
      onInlineClose?.()
    } else {
      onClose?.()
    }
  }

  if (!inline && !isOpen) return null

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitError(null)

    try {
      const payload = { ...formData }
      if (formData.pod_type === 'INTERRUPTABLE' && customBidEnabled) {
        const parsedBid = parseFloat(customBidValue)
        if (!Number.isNaN(parsedBid) && parsedBid > 0) {
          payload.bid_per_gpu = parsedBid
        }
      }
      await onCreatePod(payload)
      setSubmitError(null)
      resetForm()
      if (!inline) {
        onClose?.()
      } else {
        onInlineClose?.()
      }
    } catch (error) {
      console.error('Failed to create pod:', error)
      setSubmitError(error?.message || 'Failed to create pod, please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const selectedGpu = gpuTypes.find(gpu => gpu.id === formData.gpu_type_id)
  const baseGpuPrice = () => {
    if (!selectedGpu) return 0
    if (formData.pod_type === 'INTERRUPTABLE') {
      return formData.cloud_type === 'SECURE' ? selectedGpu.secure_spot_price : selectedGpu.community_spot_price
    } else {
      return formData.cloud_type === 'SECURE' ? selectedGpu.secure_price : selectedGpu.community_price
    }
  }
  const parsedContainerGb = Number.isFinite(Number(formData.container_disk_gb))
    ? Number(formData.container_disk_gb)
    : 0
  const parsedVolumeGb = Number.isFinite(Number(formData.volume_gb)) ? Number(formData.volume_gb) : 0
  const totalStorageGb = parsedContainerGb + parsedVolumeGb
  const storageCost = totalStorageGb * STORAGE_HOURLY_PER_GB
  const gpuCount = Number.isFinite(Number(formData.gpu_count)) ? Number(formData.gpu_count) : 1
  const gpuHourlyCost = (baseGpuPrice() || 0) * gpuCount
  const totalEstimate = gpuHourlyCost + storageCost
  const toDaily = (value) => value * 24
  const toMonthly = (value) => value * 24 * 30
  const defaultSpotBid = formData.pod_type === 'INTERRUPTABLE' ? baseGpuPrice() : null

  const sectionClass = inline ? 'pod-card-section space-y-3 w-full' : 'space-y-4'
  const gridClass = inline ? 'grid grid-cols-2 gap-3 text-sm' : 'grid grid-cols-2 gap-4'
  const containerClass = inline ? 'card pod-card' : 'modal-content'

  const content = (
    <div className={containerClass}>
      <div className={inline ? 'flex justify-between items-start gap-3' : 'mb-4'}>
        <div>
          <h2 className={inline ? 'text-base font-semibold' : 'text-xl font-bold'}>Create New Pod</h2>
          {inline && (
            <p className="text-xs text-gray-500">
              Configure specs, preview the hourly/daily cost, then launch when ready.
            </p>
          )}
        </div>
      </div>

      <form onSubmit={handleSubmit} className={inline ? 'flex flex-col gap-4' : 'space-y-4 flex-1 flex flex-col'}>
        <div className={sectionClass}>
          <label className="block text-sm font-medium mb-1">Pod Name</label>
          <input
            type="text"
            value={formData.pod_name}
            onChange={(e) => setFormData({ ...formData, pod_name: e.target.value })}
            className="input-field"
            required
          />
        </div>

        <div className={sectionClass}>
          <label className="block text-sm font-medium mb-1">GPU Type</label>
          <select
            value={formData.gpu_type_id}
            onChange={(e) => {
              setSubmitError(null)
              setFormData({ ...formData, gpu_type_id: e.target.value })
            }}
            className="input-field text-sm"
            required
          >
            <option value="">Select GPU Type</option>
            {gpuTypes.map((gpu) => {
              const priceForGpu = (currentGpu) => {
                if (formData.pod_type === 'INTERRUPTABLE') {
                  return formData.cloud_type === 'SECURE'
                    ? currentGpu.secure_spot_price
                    : currentGpu.community_spot_price
                } else {
                  return formData.cloud_type === 'SECURE'
                    ? currentGpu.secure_price
                    : currentGpu.community_price
                }
              }

              return (
                <option key={gpu.id} value={gpu.id}>
                  {gpu.display_name} - {gpu.memory_gb}GB - ${priceForGpu(gpu)?.toFixed(3) || '0.000'}/hr
                  {gpu.stock_status === 'Low' && ' (Low Stock)'}
                  {!gpu.available_community && !gpu.available_secure && ' (Unavailable)'}
                </option>
              )
            })}
          </select>
        </div>

        <div className={sectionClass}>
          <div className={gridClass}>
            <div>
              <label className="block text-sm font-medium mb-1">Pod Type</label>
              <select
                value={formData.pod_type}
                onChange={(e) => setFormData({ ...formData, pod_type: e.target.value })}
                className="input-field text-sm"
              >
                <option value="ON_DEMAND">On-Demand (Guaranteed)</option>
                <option value="INTERRUPTABLE">Spot Instance (Cheaper)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Cloud Type</label>
              <input
                type="text"
                value="Community (default)"
                className="input-field bg-gray-100 text-gray-500 text-sm"
                disabled
              />
              <p className="text-xs text-gray-500 mt-1">
                RunPod does not report the final cloud tier yet, so we request Community by default.
              </p>
            </div>
          </div>
        </div>

        <div className={sectionClass}>
          <div className="grid grid-cols-3 gap-3 text-sm">
            <div>
              <label className="block text-xs font-medium mb-1">GPU Count</label>
              <input
                type="number"
                min="1"
                max={selectedGpu?.max_community_count || 8}
                value={formData.gpu_count}
                onChange={(e) => setFormData({ ...formData, gpu_count: parseInt(e.target.value) })}
                className="input-field text-sm w-[96px]"
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Container Disk (GB)</label>
              <input
                type="number"
                min="10"
                value={formData.container_disk_gb}
                onChange={(e) => setFormData({ ...formData, container_disk_gb: parseInt(e.target.value) })}
                className="input-field text-sm w-[96px]"
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Volume (GB)</label>
              <input
                type="number"
                min="0"
                value={formData.volume_gb}
                onChange={(e) => setFormData({ ...formData, volume_gb: parseInt(e.target.value) })}
                className="input-field text-sm w-[96px]"
              />
            </div>
          </div>
        </div>

        <div className={`${sectionClass} bg-blue-50 rounded-lg border border-blue-100 text-sm text-blue-900 px-5 py-4`}>
          <div className="flex justify-between font-semibold">
            <span>GPU ({gpuCount} × {selectedGpu?.display_name || 'Select GPU'})</span>
            <span>${gpuHourlyCost.toFixed(3)}/hr</span>
          </div>
          <div className="flex justify-between text-xs text-blue-700">
            <span>Daily / Monthly</span>
            <span>${toDaily(gpuHourlyCost).toFixed(2)} / ${toMonthly(gpuHourlyCost).toFixed(2)}</span>
          </div>
          <div className="flex justify-between font-semibold pt-3">
            <span>Storage ({totalStorageGb} GB @ ${STORAGE_MONTHLY_USD_PER_GB}/GB-mo)</span>
            <span>${storageCost.toFixed(3)}/hr</span>
          </div>
          <div className="flex justify-between text-xs text-blue-700">
            <span>Daily / Monthly</span>
            <span>${toDaily(storageCost).toFixed(2)} / ${toMonthly(storageCost).toFixed(2)}</span>
          </div>
          <div className="flex justify-between border-t border-blue-200 pt-3 text-base font-semibold">
            <span>Total Estimated</span>
            <span>${totalEstimate.toFixed(3)}/hr</span>
          </div>
          <div className="flex justify-between text-xs text-blue-700">
            <span>Daily / Monthly</span>
            <span>${toDaily(totalEstimate).toFixed(2)} / ${toMonthly(totalEstimate).toFixed(2)}</span>
          </div>
          <div className="pt-3 text-xs text-blue-900">
            <p>Pod Type: {formData.pod_type === 'INTERRUPTABLE' ? 'Spot (Interruptible)' : 'On-Demand'}</p>
            <p>Cloud Type: {formData.cloud_type === 'SECURE' ? 'Secure' : 'Community'}</p>
          </div>
        </div>

        {formData.pod_type === 'INTERRUPTABLE' && (
          <div className={`${sectionClass} bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-900 space-y-3`}>
            <div className="flex justify-between items-center">
              <div>
                <p className="font-semibold">Spot bidding</p>
                <p className="text-xs">
                  Default bid per GPU:{' '}
                  {selectedGpu && defaultSpotBid ? `$${defaultSpotBid.toFixed(3)}/hr` : 'Select a GPU'}
                </p>
              </div>
            </div>
            <label className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={customBidEnabled}
                onChange={(e) => setCustomBidEnabled(e.target.checked)}
              />
              Override bid per GPU
            </label>
            {customBidEnabled && (
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                  step="0.001"
                  value={customBidValue}
                  onChange={(e) => setCustomBidValue(e.target.value)}
                  className="input-field"
                  placeholder="e.g. 1.250"
                />
                <span className="text-xs">USD/hr</span>
              </div>
            )}
            <p className="text-xs">
              If a spot request fails due to limited capacity, try increasing the bid above the current marketplace price or switch to On-Demand for guaranteed availability.
            </p>
          </div>
        )}

        <div className="pt-4 space-y-3">
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleCancel}
              className="btn-secondary btn-md flex-1"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            {inline && (
              <button
                type="button"
                onClick={handleClear}
                className="btn-secondary btn-md flex-1"
                disabled={isSubmitting}
              >
                Clear
              </button>
            )}
          </div>
          <div className="flex flex-col gap-2">
            {submitError && (
              <div className="rounded-md border border-yellow-400 bg-yellow-50 px-3 py-2 text-sm font-semibold text-yellow-900">
                {submitError}
              </div>
            )}
            <button
              type="submit"
              className="btn-primary btn-md w-full"
              disabled={isSubmitting || !formData.gpu_type_id}
            >
              {isSubmitting ? <LoadingSpinner /> : 'Create Pod'}
            </button>
          </div>
        </div>
      </form>
    </div>
  )

  if (inline) {
    return content
  }

  return (
    <div className="modal-overlay">
      {content}
    </div>
  )
}

// Enhanced Pod component with comprehensive information
const PodComponent = ({
  pod,
  onAction,
  isActing,
  gpuTypeMap,
  profiles,
  assignedProfileId,
  onAssignProfile,
  onBackupNow,
  isBackingUp,
  defaultCategories,
  categoryOptions,
  selectedCategories,
  onToggleCategory,
  onResetCategories,
  onApplyCategoriesToProfile,
  canPersistCategories,
  profileName,
  recipeName,
  onOpenRestore,
  connectionInfo
}) => {
  const podRuntime = pod.runtime || {}
  const hasConnectionData =
    (Array.isArray(podRuntime.ports) && podRuntime.ports.some((port) => port?.publicPort || port?.ip)) ||
    (Array.isArray(podRuntime.gpus) && podRuntime.gpus.length > 0)
  const isProvisioning = pod.desiredStatus === 'RUNNING' && !hasConnectionData
  const statusForBadge = isProvisioning ? 'INITIALIZING' : pod.desiredStatus

  const resolvedGpuType = pod.gpuTypeId ? gpuTypeMap?.[pod.gpuTypeId] : null
  const gpuDisplayName = resolvedGpuType?.display_name || pod.machine?.gpuDisplayName || 'Unknown'
  const [isCategoriesOpen, setIsCategoriesOpen] = useState(false)
  const activeCategories = selectedCategories || []
  const getActionButtons = () => {
    const status = pod.desiredStatus
    const buttons = []

    if (status === 'RUNNING') {
      buttons.push(
        <button
          key="stop"
          onClick={() => onAction(pod.id, 'stop')}
          className="btn-warning btn-sm"
          disabled={isActing}
          title="Stop pod (data preserved)"
        >
          <Square className="w-3 h-3 mr-1" />
          Stop
        </button>
      )
    }

    if (status === 'EXITED' || status === 'STOPPED') {
      buttons.push(
        <button
          key="resume"
          onClick={() => onAction(pod.id, 'resume')}
          className="btn-success btn-sm"
          disabled={isActing}
          title="Resume pod"
        >
          <Play className="w-3 h-3 mr-1" />
          Resume
        </button>
      )
    }

    if (status !== 'TERMINATED') {
      const canTerminate = status === 'STOPPED' || status === 'EXITED'
      buttons.push(
        <button
          key="terminate"
          onClick={() => onAction(pod.id, 'terminate')}
          className="btn-danger btn-sm"
          disabled={isActing || !canTerminate}
          title={
            canTerminate
              ? 'Terminate pod (data deleted)'
              : 'Stop the pod before terminating to avoid data loss'
          }
          style={!canTerminate ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
        >
          <Trash2 className="w-3 h-3 mr-1" />
          Terminate
        </button>
      )
    }

    return buttons
  }
  const handleRestoreClick = () => {
    if (typeof onOpenRestore === 'function') {
      onOpenRestore(pod)
    }
  }

  const formatUptime = (seconds) => {
    if (!seconds) return 'N/A'
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    return `${hours}h ${minutes}m`
  }

  const getPodTypeDisplay = (podType) => {
    if (podType === 'INTERRUPTABLE') return 'Spot Instance'
    if (podType === 'RESERVED') return 'Reserved Instance'
    return 'On-Demand'
  }
  const httpEndpoint = connectionInfo?.http_endpoint
  const demoComfyUrl = DEMO_MODE ? '/comfyui-demo.png' : null
  const openComfyUrl = httpEndpoint?.proxy_url || httpEndpoint?.url || demoComfyUrl

  return (
    <div className="card pod-card">
      <div className="flex justify-between items-start gap-3">
        <div>
          <h3 className="text-base font-semibold">{pod.name}</h3>
          <p className="text-xs text-gray-500 break-all">ID: {pod.id}</p>
        </div>
        <StatusBadge status={statusForBadge} />
      </div>
      {isProvisioning && (
        <div className="pod-card-section rounded-md bg-yellow-50 px-3 py-2 text-xs text-yellow-900 border border-yellow-100">
          <div className="flex items-start gap-2">
            <Clock className="w-4 h-4 mt-0.5" />
            <span>
              RunPod is still attaching connection data. The pod flips to <strong>Running</strong> once tunnel ports publish.
            </span>
          </div>
        </div>
      )}

      <div className="pod-card-section">
        <div className="grid grid-cols-2 gap-3 text-xs sm:text-sm">
          <div>
            <p className="pod-metric-label">GPUs</p>
            <p className="pod-metric-value">{pod.gpuCount || 0}</p>
          </div>
          <div>
            <p className="pod-metric-label">GPU Model</p>
            <p className="pod-metric-value">{gpuDisplayName}</p>
          </div>
          <div>
            <p className="pod-metric-label">Pod Type</p>
            <p className="pod-metric-value">{getPodTypeDisplay(pod.podType)}</p>
          </div>
          <div>
            <p className="pod-metric-label">Cloud</p>
            <p className="pod-metric-value">{pod.cloudType || 'Unknown'}</p>
          </div>
          <div>
            <p className="pod-metric-label">Cost / Hr</p>
            <p className="pod-metric-value">${pod.costPerHr?.toFixed(3) || '0.000'}</p>
          </div>
          <div>
            <p className="pod-metric-label">Uptime</p>
            <p className="pod-metric-value">{formatUptime(pod.uptimeSeconds)}</p>
          </div>
        </div>
      </div>

      <div className="pod-card-section">
        <div className="grid grid-cols-2 gap-3 text-xs sm:text-sm">
          <div>
            <p className="pod-metric-label">Memory</p>
            <p className="pod-metric-value">{pod.memoryInGb || 0} GB</p>
          </div>
          <div>
            <p className="pod-metric-label">vCPUs</p>
            <p className="pod-metric-value">{pod.vcpuCount || 0}</p>
          </div>
          <div>
            <p className="pod-metric-label">Container Disk</p>
            <p className="pod-metric-value">{pod.containerDiskInGb || 0} GB</p>
          </div>
          <div>
            <p className="pod-metric-label">Volume</p>
            <p className="pod-metric-value">{pod.volumeInGb || 0} GB</p>
          </div>
        </div>
      </div>

      <div className="pod-card-section space-y-2">
        <p className="pod-metric-label">Template</p>
        <p className="text-xs font-medium text-gray-800 break-all">{pod.imageName || 'N/A'}</p>
      </div>

      <div className="pod-card-section space-y-3">
        <div className="flex flex-col gap-2">
          <span className="pod-metric-label">Autosave Profile</span>
          <select
            className="input-field text-sm"
            value={assignedProfileId || ''}
            onChange={(e) => onAssignProfile(pod.id, e.target.value || null)}
          >
            <option value="">
              Global default ({defaultCategories.join(', ')})
            </option>
            {profiles.map((profile) => (
              <option key={profile.id} value={profile.id}>
                {profile.name} ({profile.cadence_minutes}m)
              </option>
            ))}
          </select>
        </div>
        <button
          className="btn-secondary btn-sm w-full"
          onClick={() => onBackupNow(pod)}
          disabled={isBackingUp}
        >
          {isBackingUp ? (
            <span className="flex items-center gap-2 justify-center">
              <LoadingSpinner />
              Backing up...
            </span>
          ) : (
            'Backup Now'
          )}
        </button>
      </div>

      <div className="pod-card-section space-y-2">
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div>
            <span className="pod-metric-label">Environment Recipe</span>
            <p className="pod-metric-value">{recipeName || 'Not assigned'}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button className="btn-secondary btn-sm" onClick={handleRestoreClick}>
              <RotateCcw className="w-3 h-3 mr-1" />
              Restore
            </button>
            {openComfyUrl && (
              <a
                className="btn-primary btn-sm inline-flex items-center"
                href={openComfyUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Globe className="w-3 h-3 mr-1" />
                Open ComfyUI
              </a>
            )}
          </div>
        </div>
        <p className="text-[0.7rem] text-gray-500">
          {openComfyUrl
            ? 'Restore recipes or open the detected HTTP service to start working immediately.'
            : 'Replays recipe assets plus selected snapshot categories into this pod’s workspace.'}
        </p>
      </div>

      <div className="pod-card-section space-y-2">
        <button
          type="button"
          className="text-sm font-medium text-blue-600 hover:underline text-left"
          onClick={() => setIsCategoriesOpen((prev) => !prev)}
        >
          {isCategoriesOpen ? 'Hide backup customization' : 'Customize backup contents'}
        </button>
        {isCategoriesOpen && (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-3">
            <p className="text-xs text-gray-600">
              Choose which categories to include when running manual backups. Autosaves continue to use the selected profile.
            </p>
            <div className="flex flex-col gap-2 text-sm">
              {categoryOptions.map((option) => (
                <label key={`${pod.id}-${option.id}`} className="inline-flex items-center gap-2">
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={activeCategories.includes(option.id)}
                    onChange={() => onToggleCategory(pod.id, option.id)}
                  />
                  {option.label}
                </label>
              ))}
            </div>
            <div className="flex flex-wrap gap-3 text-xs text-gray-600">
              <button
                type="button"
                className="text-blue-600 hover:underline"
                onClick={() => onResetCategories(pod.id)}
              >
                Reset to {profileName || 'global default'}
              </button>
              {canPersistCategories && activeCategories.length > 0 && (
                <button
                  type="button"
                  className="text-blue-600 hover:underline"
                  onClick={() => onApplyCategoriesToProfile(pod.id)}
                >
                  Apply these categories to profile
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="pod-card-section border-none pt-0">
        <div className="flex flex-wrap gap-2">
          {isActing ? (
            <div className="flex items-center text-blue-600 text-sm">
              <LoadingSpinner />
              <span className="ml-2">Processing...</span>
            </div>
          ) : (
            getActionButtons()
          )}
        </div>
        {pod.desiredStatus !== 'TERMINATED' && (
          <div className="mt-3 flex items-start gap-2 rounded-md bg-yellow-50 px-3 py-2 text-[0.72rem] text-yellow-900">
            <AlertTriangle className="w-4 h-4 mt-0.5" />
            <span>
              Stopping preserves disk data but does <strong>not</strong> guarantee future GPU slots. Back up S3 artifacts before stopping to avoid loss.
            </span>
          </div>
        )}
      </div>
    </div>
  )
}

const AutomationProfilesModal = ({
  isOpen,
  onClose,
  profiles,
  formState,
  onFieldChange,
  onSubmit,
  isSaving,
  onEdit,
  onDelete,
  editingProfileId,
  profileDefaults
}) => {
  if (!isOpen) return null

  return (
    <div className="modal-overlay">
      <div className="modal-content max-w-3xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">Automation Profiles</h2>
          <button className="btn-secondary btn-sm" onClick={onClose}>
            Close
          </button>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          Profiles control autosave cadence, categories, and S3 destinations. Assign them to pods to override the global default ({(profileDefaults.categories || []).join(', ')} every {profileDefaults.cadence_minutes}m).
        </p>
        <div className="mb-6">
          {profiles.length === 0 ? (
            <div className="text-sm text-gray-600">No profiles yet. Use the form below to create one.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500">
                    <th className="py-2">Name</th>
                    <th className="py-2">Cadence</th>
                    <th className="py-2">Categories</th>
                    <th className="py-2">S3 Prefix</th>
                    <th className="py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {profiles.map((profile) => (
                    <tr key={profile.id} className="border-t">
                      <td className="py-2 font-medium">{profile.name}</td>
                      <td className="py-2">{profile.cadence_minutes} min</td>
                      <td className="py-2">{(profile.categories || []).join(', ')}</td>
                      <td className="py-2 text-xs break-all">{profile.s3_prefix || 'Default'}</td>
                      <td className="py-2 flex justify-end gap-2">
                        <button className="btn-secondary btn-xs" onClick={() => onEdit(profile)}>
                          Edit
                        </button>
                        <button className="btn-danger btn-xs" onClick={() => onDelete(profile.id, profile.name)}>
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault()
            onSubmit()
          }}
          className="space-y-4"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Name</label>
              <input
                type="text"
                className="input-field"
                value={formState.name}
                onChange={(e) => onFieldChange('name', e.target.value)}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Cadence (minutes)</label>
              <input
                type="number"
                min={1}
                className="input-field"
                value={formState.cadence_minutes}
                onChange={(e) => onFieldChange('cadence_minutes', e.target.value)}
                required
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Categories (comma separated)</label>
            <input
              type="text"
              className="input-field"
              value={formState.categories}
              onChange={(e) => onFieldChange('categories', e.target.value)}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">S3 Prefix (optional)</label>
              <input
                type="text"
                className="input-field"
                value={formState.s3_prefix}
                onChange={(e) => onFieldChange('s3_prefix', e.target.value)}
                placeholder="comfyui-backups/profiles/team-a"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Retention (days, optional)</label>
              <input
                type="number"
                min={1}
                className="input-field"
                value={formState.retention_days}
                onChange={(e) => onFieldChange('retention_days', e.target.value)}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Notes</label>
            <textarea
              className="input-field"
              rows={3}
              value={formState.notes}
              onChange={(e) => onFieldChange('notes', e.target.value)}
              placeholder="Describe when to use this profile"
            />
          </div>
          <div className="flex justify-end gap-3">
            <button type="button" className="btn-secondary btn-sm" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-primary btn-sm" disabled={isSaving}>
              {isSaving ? 'Saving...' : editingProfileId ? 'Update Profile' : 'Create Profile'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

const RestoreModal = ({
  isOpen,
  pod,
  recipes,
  categoryOptions,
  formState,
  onClose,
  onSubmit,
  onFieldChange,
  onToggleCategory,
  isSubmitting,
  jobHistory
}) => {
  if (!isOpen || !pod) return null
  const disableSubmit = !formState.recipeId || !formState.snapshotPrefix
  const activeCategories = formState.categories || []

  return (
    <div className="modal-overlay">
      <div className="modal-content max-w-3xl">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-blue-600" />
              Restore & Recipe Staging
            </h2>
            <p className="text-sm text-gray-600">
              Target pod: <span className="font-semibold">{pod.name || pod.id}</span>
            </p>
          </div>
          <button className="btn-secondary btn-sm" onClick={onClose}>
            Close
          </button>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Recipe</label>
              <select
                className="input-field"
                value={formState.recipeId}
                onChange={(e) => onFieldChange('recipeId', e.target.value)}
              >
                <option value="">Select a recipe</option>
                {recipes.map((recipe) => (
                  <option key={recipe.id} value={recipe.id}>
                    {recipe.name} • {(recipe.tags || []).join(', ') || 'untagged'}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Recipes capture base images, default models, extensions, and bootstrap scripts.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Snapshot Prefix</label>
              <input
                type="text"
                className="input-field"
                placeholder="backups/prod/pod-123/2025/03/05/autosave-..."
                value={formState.snapshotPrefix}
                onChange={(e) => onFieldChange('snapshotPrefix', e.target.value)}
              />
              <p className="text-xs text-gray-500 mt-1">
                Paste the manifest prefix from S3. The backend pulls only the enabled categories.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Target Path (optional)</label>
              <input
                type="text"
                className="input-field"
                placeholder="Defaults to ./data/pod_mounts/&lt;pod-id&gt;"
                value={formState.targetPath}
                onChange={(e) => onFieldChange('targetPath', e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-2">
              <label className="block text-sm font-medium">Restore Options</label>
              <label className="inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={formState.dryRun}
                  onChange={(e) => onFieldChange('dryRun', e.target.checked)}
                />
                Dry-run (stage files only)
              </label>
              <label className="inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={formState.cleanSync}
                  onChange={(e) => onFieldChange('cleanSync', e.target.checked)}
                />
                Clean sync (replace existing folders)
              </label>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Categories to restore</label>
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
              {categoryOptions.map((option) => (
                <label key={`restore-${option.id}`} className="inline-flex items-center gap-2 text-sm mr-4 mb-2">
                  <input
                    type="checkbox"
                    checked={activeCategories.includes(option.id)}
                    onChange={() => onToggleCategory(option.id)}
                  />
                  {option.label}
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Operator Notes</label>
            <textarea
              className="input-field"
              rows={3}
              placeholder="Document why this restore was triggered"
              value={formState.notes}
              onChange={(e) => onFieldChange('notes', e.target.value)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2 flex items-center gap-2">
              <History className="w-4 h-4" />
              Recent jobs for this pod
            </label>
            {jobHistory?.length ? (
              <ul className="space-y-2 max-h-40 overflow-y-auto">
                {jobHistory.map((job) => (
                  <li key={job.id} className="rounded-md border border-gray-200 p-2 text-xs">
                    <div className="flex justify-between">
                      <span className="font-semibold capitalize">{job.status}</span>
                      <span className="text-gray-500">
                        {job.updated_at ? new Date(job.updated_at).toLocaleTimeString() : '—'}
                      </span>
                    </div>
                    <p className="text-gray-600">
                      Recipe #{job.recipe_id ? job.recipe_id.slice(0, 6) : 'unknown'} • Snapshot {job.snapshot_prefix?.split('/').pop() || 'n/a'}
                    </p>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-gray-500">No restore jobs yet.</p>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button type="button" className="btn-secondary btn-sm" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary btn-sm"
            disabled={disableSubmit || isSubmitting}
            onClick={onSubmit}
          >
            {isSubmitting ? <LoadingSpinner /> : 'Run Restore'}
          </button>
        </div>
      </div>
    </div>
  )
}

// Main application component
function App() {
  const [apiConfig, setApiConfig] = useState(() => {
    if (DEMO_MODE) {
      return { apiBaseUrl: DEMO_API_LABEL, apiToken: '' }
    }
    if (typeof window === 'undefined') {
      return { apiBaseUrl: DEFAULT_API_BASE_URL, apiToken: DEFAULT_API_TOKEN }
    }
    try {
      const stored = window.localStorage.getItem(API_CONFIG_STORAGE_KEY)
      if (stored) {
        return JSON.parse(stored)
      }
    } catch (err) {
      console.warn('Unable to read stored API config', err)
    }
    return { apiBaseUrl: DEFAULT_API_BASE_URL, apiToken: DEFAULT_API_TOKEN }
  })
  const [notifications, setNotifications] = useState([])
  const dismissToast = useCallback((id) => {
    setNotifications((prev) => prev.filter((toast) => toast.id !== id))
  }, [])
  const pushNotification = useCallback(
    (type, message) => {
      const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`
      setNotifications((prev) => [...prev, { id, type, message }])
      setTimeout(() => dismissToast(id), 5000)
    },
    [dismissToast]
  )
  const [recipeAssignments, setRecipeAssignments] = useState(() => {
    if (typeof window === 'undefined') {
      return {}
    }
    try {
      const stored = window.localStorage.getItem(RECIPE_ASSIGNMENTS_STORAGE_KEY)
      return stored ? JSON.parse(stored) : {}
    } catch (err) {
      console.warn('Unable to read stored recipe assignments', err)
      return {}
    }
  })
  useEffect(() => {
    if (typeof window === 'undefined') return
    if (DEMO_MODE) return
    window.localStorage.setItem(API_CONFIG_STORAGE_KEY, JSON.stringify(apiConfig))
  }, [apiConfig])
  useEffect(() => {
    if (typeof window === 'undefined') return
    window.localStorage.setItem(RECIPE_ASSIGNMENTS_STORAGE_KEY, JSON.stringify(recipeAssignments))
  }, [recipeAssignments])
  const api = useMemo(() => {
    if (DEMO_MODE) {
      return createMockApiClient()
    }
    return createApiClient(apiConfig)
  }, [apiConfig])

  const [pods, setPods] = useState([])
  const [costs, setCosts] = useState(null)
  const [health, setHealth] = useState(null)
  const [gpuTypes, setGpuTypes] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isTemplateWizardOpen, setIsTemplateWizardOpen] = useState(false)
  const [showInlineCreator, setShowInlineCreator] = useState(false)
  const [showEmptyStateCreator, setShowEmptyStateCreator] = useState(true)
  const [actingPods, setActingPods] = useState(new Set())
  const [isSettingsOpen, setIsSettingsOpen] = useState(false)
  const [lastUpdated, setLastUpdated] = useState(null)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [backupProfiles, setBackupProfiles] = useState([])
  const [profileBindings, setProfileBindings] = useState({})
  const [profileDefaults, setProfileDefaults] = useState(DEFAULT_AUTOSAVE_CONFIG)
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false)
  const [profileForm, setProfileForm] = useState(PROFILE_FORM_TEMPLATE)
  const [editingProfileId, setEditingProfileId] = useState(null)
  const [isSavingProfile, setIsSavingProfile] = useState(false)
  const [backingUpPods, setBackingUpPods] = useState(new Set())
  const [customCategories, setCustomCategories] = useState({})
  const [recipes, setRecipes] = useState([])
  const [isRecipeLoading, setIsRecipeLoading] = useState(false)
  const [restoreJobs, setRestoreJobs] = useState([])
  const [restoreModalState, setRestoreModalState] = useState({ isOpen: false, pod: null })
  const [restoreForm, setRestoreForm] = useState(DEFAULT_RESTORE_STATE)
  const [isSubmittingRestore, setIsSubmittingRestore] = useState(false)
  const [connectionData, setConnectionData] = useState({
    pods: [],
    ssh_key_configured: false,
    ssh_key_path: '',
    ssh_user: 'root',
    ssh_remote_path: '/workspace',
    bootstrap_command: ''
  })
  const [isConnectionLoading, setIsConnectionLoading] = useState(false)
  const [recentEvents, setRecentEvents] = useState([])
  const [isEventsExpanded, setIsEventsExpanded] = useState(false)
  const connectionMap = useMemo(() => {
    const map = {}
    if (connectionData?.pods) {
      connectionData.pods.forEach((entry) => {
        map[entry.pod_id] = entry
      })
    }
    return map
  }, [connectionData])
  const gpuTypeMap = useMemo(() => {
    return gpuTypes.reduce((acc, type) => {
      if (type?.id) {
        acc[type.id] = type
      }
      return acc
    }, {})
  }, [gpuTypes])
  const recipeLookup = useMemo(() => {
    return recipes.reduce((acc, recipe) => {
      if (recipe?.id) {
        acc[recipe.id] = recipe
      }
      return acc
    }, {})
  }, [recipes])
  const applyProfilesPayload = useCallback((payload) => {
    if (!payload) return
    setBackupProfiles(payload.profiles || [])
    setProfileBindings(payload.bindings || {})
    setProfileDefaults(payload.defaults || DEFAULT_AUTOSAVE_CONFIG)
  }, [])
  const resolveRecipeName = useCallback(
    (podId) => {
      const recipeId = recipeAssignments[podId]
      if (!recipeId) return null
      return recipeLookup[recipeId]?.name || 'Archived recipe'
    },
    [recipeAssignments, recipeLookup]
  )

  const fetchEvents = useCallback(async () => {
    try {
      const payload = await api.get('/events?limit=50')
      setRecentEvents(payload.events || [])
    } catch (err) {
      console.warn('Unable to fetch events', err)
    }
  }, [api])

  const fetchConnectionData = useCallback(async () => {
    try {
      setIsConnectionLoading(true)
      const payload = await api.get('/pods/ssh-readiness')
      setConnectionData(payload)
    } catch (err) {
      console.warn('Unable to fetch SSH readiness', err)
      pushNotification('error', err?.message || 'Unable to check SSH readiness')
    } finally {
      setIsConnectionLoading(false)
    }
  }, [api, pushNotification])

  const fetchRecipes = useCallback(
    async ({ silent = false } = {}) => {
      try {
        if (!silent) setIsRecipeLoading(true)
        const payload = await api.get('/recipes')
        setRecipes(payload.recipes || [])
      } catch (err) {
        console.warn('Unable to fetch recipes', err)
        if (!silent) {
          pushNotification('error', err?.message || 'Failed to load recipes')
        }
      } finally {
        if (!silent) setIsRecipeLoading(false)
      }
    },
    [api, pushNotification]
  )

  const fetchRestoreJobs = useCallback(async () => {
    try {
      const payload = await api.get('/recipes/restore-jobs')
      setRestoreJobs(payload.jobs || [])
    } catch (err) {
      console.warn('Unable to fetch restore jobs', err)
    }
  }, [api])

  const refreshProfiles = useCallback(async () => {
    try {
      const payload = await api.get('/automation/profiles')
      applyProfilesPayload(payload)
    } catch (err) {
      console.warn('Unable to refresh profiles', err)
    }
  }, [api, applyProfilesPayload])
  const resolveBaselineCategories = useCallback((podId) => {
    const profileId = profileBindings[podId]
    const profile = backupProfiles.find((item) => item.id === profileId)
    if (profile?.categories?.length) {
      return profile.categories
    }
    if (profileDefaults.categories?.length) {
      return profileDefaults.categories
    }
    return DEFAULT_AUTOSAVE_CONFIG.categories
  }, [profileBindings, backupProfiles, profileDefaults])

  const getCategoriesForPod = useCallback((podId) => {
    if (customCategories[podId]?.length) {
      return customCategories[podId]
    }
    return resolveBaselineCategories(podId)
  }, [customCategories, resolveBaselineCategories])
  const openRestoreModal = useCallback(
    (pod) => {
      if (!pod) return
      if (!recipes.length) {
        pushNotification('error', 'Define at least one recipe before running a restore.')
        fetchRecipes()
        return
      }
      const suggestedRecipeId = recipeAssignments[pod.id] || recipes[0]?.id || ''
      setRestoreModalState({ isOpen: true, pod })
      setRestoreForm({
        ...DEFAULT_RESTORE_STATE,
        recipeId: suggestedRecipeId,
        categories: getCategoriesForPod(pod.id),
      })
    },
    [fetchRecipes, getCategoriesForPod, pushNotification, recipeAssignments, recipes]
  )
  const closeRestoreModal = useCallback(() => {
    setRestoreModalState({ isOpen: false, pod: null })
    setRestoreForm(DEFAULT_RESTORE_STATE)
  }, [])
  const handleRestoreFieldChange = useCallback((field, value) => {
    setRestoreForm((prev) => ({ ...prev, [field]: value }))
  }, [])
  const handleRestoreCategoryToggle = useCallback((categoryId) => {
    setRestoreForm((prev) => {
      const hasCategory = prev.categories?.includes(categoryId)
      const nextCategories = hasCategory
        ? prev.categories.filter((item) => item !== categoryId)
        : [...(prev.categories || []), categoryId]
      return { ...prev, categories: nextCategories }
    })
  }, [])
  const handleRestoreSubmit = useCallback(async () => {
    const targetPod = restoreModalState.pod
    if (!targetPod) return
    if (!restoreForm.recipeId) {
      pushNotification('error', 'Pick a recipe before running the restore.')
      return
    }
    if (!restoreForm.snapshotPrefix?.trim()) {
      pushNotification('error', 'Snapshot prefix is required.')
      return
    }
    setIsSubmittingRestore(true)
    try {
      const payload = {
        pod_id: targetPod.id,
        pod_name: targetPod.name,
        snapshot_prefix: restoreForm.snapshotPrefix.trim(),
        categories: restoreForm.categories.length ? restoreForm.categories : undefined,
        target_path: restoreForm.targetPath || undefined,
        dry_run: restoreForm.dryRun,
        clean_sync: restoreForm.cleanSync,
        notes: restoreForm.notes,
      }
      const response = await api.post(`/recipes/${restoreForm.recipeId}/restore`, payload)
      const jobId = response?.job?.id
      pushNotification('success', jobId ? `Restore job #${jobId} submitted` : 'Restore submitted')
      if (!restoreForm.dryRun) {
        setRecipeAssignments((prev) => ({
          ...prev,
          [targetPod.id]: restoreForm.recipeId
        }))
      }
      closeRestoreModal()
      fetchRestoreJobs()
    } catch (err) {
      pushNotification('error', err?.message || 'Restore failed')
    } finally {
      setIsSubmittingRestore(false)
    }
  }, [
    api,
    closeRestoreModal,
    fetchRestoreJobs,
    pushNotification,
    restoreForm,
    restoreModalState,
    setRecipeAssignments
  ])
  const restoreTargetPodId = restoreModalState?.pod?.id
  const currentPodJobHistory = useMemo(() => {
    if (!restoreTargetPodId) return []
    return restoreJobs.filter((job) => job.pod_id === restoreTargetPodId).slice(0, 5)
  }, [restoreJobs, restoreTargetPodId])
  const resetProfileForm = useCallback(() => {
    setProfileForm({
      ...PROFILE_FORM_TEMPLATE,
      cadence_minutes: profileDefaults.cadence_minutes || DEFAULT_AUTOSAVE_CONFIG.cadence_minutes,
      categories: (profileDefaults.categories || DEFAULT_AUTOSAVE_CONFIG.categories).join(', ')
    })
    setEditingProfileId(null)
  }, [profileDefaults])

  const handleProfileModalClose = useCallback(() => {
    resetProfileForm()
    setIsProfileModalOpen(false)
  }, [resetProfileForm])

  const handleProfileFieldChange = useCallback((field, value) => {
    setProfileForm((prev) => ({ ...prev, [field]: value }))
  }, [])

  const handleProfileEdit = useCallback((profile) => {
    setEditingProfileId(profile.id)
    setProfileForm({
      name: profile.name,
      cadence_minutes: profile.cadence_minutes,
      categories: (profile.categories || []).join(', '),
      s3_prefix: profile.s3_prefix || '',
      retention_days: profile.retention_days ? String(profile.retention_days) : '',
      notes: profile.notes || ''
    })
    setIsProfileModalOpen(true)
  }, [])

  const handleProfileDelete = useCallback(async (profileId, name) => {
    if (!window.confirm(`Delete profile "${name}"? This cannot be undone.`)) return
    try {
      await api.delete(`/automation/profiles/${profileId}`)
      pushNotification('success', 'Profile deleted')
      await refreshProfiles()
    } catch (err) {
      pushNotification('error', `Failed to delete profile: ${err.message}`)
    }
  }, [api, pushNotification, refreshProfiles])

  const handleProfileSave = useCallback(async () => {
    const payload = {
      name: profileForm.name.trim(),
      cadence_minutes: Number(profileForm.cadence_minutes) || DEFAULT_AUTOSAVE_CONFIG.cadence_minutes,
      categories: profileForm.categories.split(',').map((item) => item.trim()).filter(Boolean),
      s3_prefix: profileForm.s3_prefix.trim() || null,
      retention_days: profileForm.retention_days ? Number(profileForm.retention_days) : null,
      notes: profileForm.notes.trim() || null
    }
    if (!payload.name) {
      pushNotification('error', 'Profile name is required')
      return
    }
    if (!payload.categories.length) {
      pushNotification('error', 'At least one category is required')
      return
    }
    try {
      setIsSavingProfile(true)
      if (editingProfileId) {
        await api.put(`/automation/profiles/${editingProfileId}`, payload)
        pushNotification('success', 'Profile updated')
      } else {
        await api.post('/automation/profiles', payload)
        pushNotification('success', 'Profile created')
      }
      await refreshProfiles()
      resetProfileForm()
      setIsProfileModalOpen(false)
    } catch (err) {
      pushNotification('error', `Failed to save profile: ${err.message}`)
    } finally {
      setIsSavingProfile(false)
    }
  }, [api, editingProfileId, profileForm, pushNotification, refreshProfiles, resetProfileForm])

  const handleCategoryToggle = useCallback((podId, category) => {
    setCustomCategories((prev) => {
      const base = prev[podId] ? [...prev[podId]] : [...resolveBaselineCategories(podId)]
      const exists = base.includes(category)
      const next = exists ? base.filter((item) => item !== category) : [...base, category]
      return { ...prev, [podId]: next }
    })
  }, [resolveBaselineCategories])

  const handleResetCategories = useCallback((podId) => {
    setCustomCategories((prev) => {
      if (!prev[podId]) return prev
      const next = { ...prev }
      delete next[podId]
      return next
    })
  }, [])

  const handleApplyCategoriesToProfile = useCallback(async (podId) => {
    const profileId = profileBindings[podId]
    const categories = customCategories[podId]
    if (!profileId || !categories || categories.length === 0) {
      pushNotification('error', 'Select at least one category to apply to the profile')
      return
    }
    try {
      await api.put(`/automation/profiles/${profileId}`, { categories })
      pushNotification('success', 'Profile categories updated')
      await refreshProfiles()
      setCustomCategories((prev) => {
        const next = { ...prev }
        delete next[podId]
        return next
      })
    } catch (err) {
      pushNotification('error', `Failed to update profile categories: ${err.message}`)
    }
  }, [api, customCategories, profileBindings, pushNotification, refreshProfiles])

  const handleAssignProfile = useCallback(async (podId, profileId) => {
    try {
      await api.post(`/pods/${podId}/profile`, { profile_id: profileId })
      pushNotification('success', profileId ? 'Profile assigned' : 'Profile cleared')
      await refreshProfiles()
      setCustomCategories((prev) => {
        const next = { ...prev }
        delete next[podId]
        return next
      })
    } catch (err) {
      pushNotification('error', `Failed to update profile: ${err.message}`)
    }
  }, [api, pushNotification, refreshProfiles])

  const handleBackupNow = useCallback(async (pod) => {
    const podId = pod.id
    setBackingUpPods((prev) => {
      const next = new Set(prev)
      next.add(podId)
      return next
    })
    try {
      const assignedId = profileBindings[podId]
      const profile = backupProfiles.find((item) => item.id === assignedId)
      const categorySource = customCategories[podId]?.length
        ? customCategories[podId]
        : profile?.categories?.length
          ? profile.categories
          : (profileDefaults.categories?.length
              ? profileDefaults.categories
              : DEFAULT_AUTOSAVE_CONFIG.categories)
      if (!categorySource.length) {
        pushNotification('error', 'Select at least one category before running a backup')
        return
      }
      const payload = {
        pod_id: podId,
        pod_name: pod.name,
        categories: categorySource,
        backup_name: `manual-${podId}-${new Date().toISOString()}`,
        notes: profile ? `Manual backup via ${profile.name}` : 'Manual backup via dashboard',
        s3_prefix: profile?.s3_prefix || null,
        retention_days: profile?.retention_days || null
      }
      await api.post('/backups/create', payload)
      pushNotification('success', 'Manual backup queued')
    } catch (err) {
      pushNotification('error', `Manual backup failed: ${err.message}`)
    } finally {
      setBackingUpPods((prev) => {
        const next = new Set(prev)
        next.delete(podId)
        return next
      })
    }
  }, [api, backupProfiles, customCategories, profileBindings, profileDefaults, pushNotification])

  const fetchData = useCallback(
    async ({ initial = false, silent = false } = {}) => {
      try {
        if (initial) setLoading(true)
        if (!silent) setIsRefreshing(true)
        setError(null)
        const [
          podsResult,
          costsResult,
          healthResult,
          gpuTypesResult,
          profilesResult
        ] = await Promise.all([
          api.get('/pods'),
          api.get('/costs'),
          api.get('/health'),
          api.get('/costs/gpu-pricing'),
          api.get('/automation/profiles')
        ])

        if (podsResult.error) {
          throw new Error(`Pods: ${podsResult.error}`)
        }
        const podList = podsResult.myself?.pods || []
        const sortedPods = [...podList].sort((a, b) => {
          const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0
          const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0
          if (aTime !== bTime) {
            return bTime - aTime
          }
          return (b.id || '').localeCompare(a.id || '')
        })
        setPods(sortedPods)

        if (costsResult.error) {
          console.warn('Costs API error:', costsResult.error)
          setCosts(null)
        } else {
          setCosts(costsResult)
        }

        setHealth(healthResult)

        if (gpuTypesResult.error) {
          console.warn('GPU Types API error:', gpuTypesResult.error)
          setGpuTypes([])
        } else {
          setGpuTypes(gpuTypesResult.gpu_types || [])
        }

        applyProfilesPayload(profilesResult)

        setLastUpdated(new Date().toISOString())
      } catch (err) {
        const message = `Dashboard refresh failed: ${err.message}`
        setError(message)
        pushNotification('error', message)
      } finally {
        if (initial) setLoading(false)
        if (!silent) setIsRefreshing(false)
      }
    },
    [api, pushNotification, applyProfilesPayload]
  )

  useEffect(() => {
    fetchData({ initial: true, silent: true })
    const interval = setInterval(() => fetchData({ silent: true }), 30000)
    return () => clearInterval(interval)
  }, [fetchData])
  useEffect(() => {
    fetchRecipes({ silent: true })
    fetchRestoreJobs()
    const interval = setInterval(() => fetchRestoreJobs(), 15000)
    return () => clearInterval(interval)
  }, [fetchRecipes, fetchRestoreJobs])
  useEffect(() => {
    fetchEvents()
    const interval = setInterval(() => fetchEvents(), 60000)
    return () => clearInterval(interval)
  }, [fetchEvents])
  useEffect(() => {
    fetchConnectionData()
    const interval = setInterval(() => fetchConnectionData(), 60000)
    return () => clearInterval(interval)
  }, [fetchConnectionData])
  const criticalEvents = useMemo(() => {
    return recentEvents.filter((event) => {
      const level = (event.level || '').toUpperCase()
      return level === 'WARNING' || level === 'ERROR' || level === 'CRITICAL'
    })
  }, [recentEvents])
  const hasCriticalEvents = criticalEvents.length > 0
  const newestEvent = criticalEvents[0] || recentEvents[0] || null

  useEffect(() => {
    if (hasCriticalEvents) {
      setIsEventsExpanded(true)
    } else {
      setIsEventsExpanded(false)
    }
  }, [hasCriticalEvents])

  const handlePodAction = useCallback(
    async (podId, action) => {
      setActingPods((prev) => {
        const next = new Set(prev)
        next.add(podId)
        return next
      })

      try {
        await api.post(`/pods/${podId}/${action}`)
        pushNotification('success', `Pod ${action} request accepted`)
        fetchData({ silent: true })
      } catch (err) {
        const rawMessage = err?.message || 'Unknown error'
        const friendly =
          action === 'terminate'
            ? 'Unable to terminate pod. Ensure it is fully stopped or retry from the RunPod console.'
            : `Failed to ${action} pod: ${rawMessage}`
        setError(friendly)
        pushNotification('error', friendly)
      } finally {
        setActingPods((prev) => {
          const next = new Set(prev)
          next.delete(podId)
          return next
        })
      }
    },
    [api, fetchData, pushNotification]
  )

  const handleCreatePod = useCallback(
    async (podConfig) => {
      setError(null)
      try {
        await api.post('/pods/create', podConfig)
        pushNotification('success', 'Pod creation request submitted')
        fetchData({ silent: true })
      } catch (err) {
        const rawMessage = err?.message || 'Unknown error'
        const isCapacity =
          rawMessage.toLowerCase().includes('no longer any instances available') ||
          rawMessage.toLowerCase().includes('capacity')
        if (isCapacity) {
          throw new Error('RunPod reports capacity exhaustion. Try a different GPU/region or switch to On-Demand.')
        }
        throw new Error(`Failed to create pod: ${rawMessage}`)
      }
    },
    [api, fetchData, pushNotification]
  )

  const handleConfigSave = (nextConfig) => {
    if (DEMO_MODE) {
      pushNotification('info', 'Demo mode uses built-in mock data; API settings are disabled.')
      setIsSettingsOpen(false)
      return
    }
    setApiConfig(nextConfig)
    setIsSettingsOpen(false)
    pushNotification('success', 'API settings updated')
  }

  const handleOpenTemplateWizard = useCallback(() => {
    setIsTemplateWizardOpen(true)
    fetchConnectionData()
  }, [fetchConnectionData])

  const handleCloseTemplateWizard = useCallback(() => {
    setIsTemplateWizardOpen(false)
  }, [])

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 p-6">
        <div className="max-w-7xl mx-auto space-y-6">
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      </div>
    )
  }

  const lastUpdatedLabel = lastUpdated ? new Date(lastUpdated).toLocaleTimeString() : 'Never'

  return (
    <>
      <ToastCenter toasts={notifications} onDismiss={dismissToast} />
      <SettingsModal
        isOpen={isSettingsOpen}
        config={apiConfig}
        onClose={() => setIsSettingsOpen(false)}
        onSave={handleConfigSave}
      />
      <AutomationProfilesModal
        isOpen={isProfileModalOpen}
        onClose={handleProfileModalClose}
        profiles={backupProfiles}
        formState={profileForm}
        onFieldChange={handleProfileFieldChange}
        onSubmit={handleProfileSave}
        isSaving={isSavingProfile}
        onEdit={handleProfileEdit}
        onDelete={handleProfileDelete}
        editingProfileId={editingProfileId}
        profileDefaults={profileDefaults}
      />
      <RestoreModal
        isOpen={restoreModalState.isOpen}
        pod={restoreModalState.pod}
        recipes={recipes}
        categoryOptions={CATEGORY_OPTIONS}
        formState={restoreForm}
        onClose={closeRestoreModal}
        onSubmit={handleRestoreSubmit}
        onFieldChange={handleRestoreFieldChange}
        onToggleCategory={handleRestoreCategoryToggle}
        isSubmitting={isSubmittingRestore}
        jobHistory={currentPodJobHistory}
      />
      <TemplateWizardModal
        isOpen={isTemplateWizardOpen}
        onClose={handleCloseTemplateWizard}
        data={connectionData}
        onRefresh={fetchConnectionData}
        isLoading={isConnectionLoading}
        notify={pushNotification}
      />
      <div className="min-h-screen bg-gray-100">
        {DEMO_MODE && (
          <div className="bg-yellow-100 border-b border-yellow-300 text-yellow-900 text-sm px-4 py-2">
            Demo mode: all data is simulated. Actions do not provision real RunPod pods or AWS resources.
          </div>
        )}
        <header className="bg-white shadow-sm border-b">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  GenAI Automation Dashboard
                </h1>
                <div className="mt-1 flex flex-wrap gap-3 text-sm text-gray-600">
                  <span className="inline-flex items-center gap-1">
                    <Globe className="w-4 h-4" />
                    {apiConfig.apiBaseUrl}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    Last updated: {lastUpdatedLabel}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => fetchData({ silent: false })}
                  className="btn-secondary btn-sm"
                  disabled={isRefreshing}
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
                  {isRefreshing ? 'Refreshing...' : 'Refresh'}
                </button>
                <button
                  onClick={() => {
                    resetProfileForm()
                    setIsProfileModalOpen(true)
                  }}
                  className="btn-secondary btn-sm"
                >
                  Automation Profiles
                </button>
                <button
                  onClick={handleOpenTemplateWizard}
                  className="btn-secondary btn-sm"
                >
                  <ShieldCheck className="w-4 h-4 mr-2" />
                  Template Wizard
                </button>
                <button
                  onClick={() => setIsSettingsOpen(true)}
                  className="btn-secondary btn-sm"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  API Settings
                </button>
              </div>
            </div>
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-4 py-6">
        {error && (
          <div className="alert-error mb-6">
            <AlertTriangle className="w-5 h-5 mr-2" />
            {error}
            <button
              onClick={() => setError(null)}
              className="ml-auto text-red-600 hover:text-red-800"
            >
              ×
            </button>
          </div>
        )}

        {/* System Health Status */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold">System Health Status</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="card pod-card pod-card-tight">
            <div className="flex items-center justify-between text-sm font-semibold text-gray-800">
              <span className="inline-flex items-center gap-2">
                <Server className="w-4 h-4 text-gray-500" />
                RunPod API
                <span className="text-xs font-normal text-gray-500">GraphQL</span>
              </span>
              <HealthBadge label="Status" status={health?.runpod_api} />
            </div>
          </div>

          <div className="card pod-card pod-card-tight">
            <div className="flex items-center justify-between text-sm font-semibold text-gray-800">
              <span className="inline-flex items-center gap-2">
                <Database className="w-4 h-4 text-gray-500" />
                AWS S3
                <span className="text-xs font-normal text-gray-500">Backups</span>
              </span>
              <HealthBadge label="Status" status={health?.aws_s3} />
            </div>
          </div>

          <div className="card pod-card pod-card-tight">
            <div className="flex items-center justify-between text-sm font-semibold text-gray-800">
              <span className="inline-flex items-center gap-2">
                <Zap className="w-4 h-4 text-gray-500" />
                GPU Marketplace
                <span className="text-xs font-normal text-gray-500">Prices</span>
              </span>
              <HealthBadge
                label="Inventory"
                status={gpuTypes.length > 0 ? 'configured' : 'unconfigured'}
              />
            </div>
            <p className="text-[0.7rem] text-gray-500">
              {gpuTypes.length > 0 ? `${gpuTypes.length} GPU types available` : 'Pricing unavailable'}
            </p>
          </div>
        </div>

        <div className="card pod-card pod-card-compact mb-8">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-800">Recent System Events</h3>
            <div className="flex items-center gap-2">
              <button className="btn-secondary btn-sm" onClick={fetchEvents}>
                Refresh
              </button>
              <button
                className="btn-secondary btn-sm"
                onClick={() => setIsEventsExpanded((prev) => !prev)}
                disabled={!recentEvents.length && !hasCriticalEvents}
              >
                {isEventsExpanded ? 'Hide events' : 'Show events'}
              </button>
            </div>
          </div>
          {!isEventsExpanded ? (
            <div className="flex items-center justify-between text-xs mt-2">
              <span className={hasCriticalEvents ? 'text-red-600 font-semibold' : 'text-green-600 font-semibold'}>
                {hasCriticalEvents
                  ? `${criticalEvents.length} issue${criticalEvents.length > 1 ? 's' : ''} detected`
                  : 'All checks passing'}
              </span>
              <span className="text-gray-500">
                {newestEvent?.created_at ? new Date(newestEvent.created_at).toLocaleTimeString() : ''}
              </span>
            </div>
          ) : criticalEvents.length === 0 ? (
            <p className="text-sm text-gray-600 mt-2">No warnings or errors logged yet.</p>
          ) : (
            <ul className="divide-y divide-gray-100 mt-3">
              {criticalEvents.slice(0, 6).map((event) => (
                <li key={event.id} className="py-2 flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-gray-800">{event.event}</p>
                    <p className="text-xs text-gray-500">
                      {event.details?.message ||
                        event.details?.error ||
                        event.details?.operation ||
                        'See backend logs for details'}
                    </p>
                  </div>
                  <div className="text-right text-xs text-gray-500">
                    <div>{event.level}</div>
                    <div>{new Date(event.created_at).toLocaleTimeString()}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Cost Summary */}
        {costs && (
          <div className="card pod-card mb-8">
            <div className="flex items-center justify-between text-sm font-semibold text-gray-800">
              <span className="inline-flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-green-500" />
                Cost Summary
              </span>
              <span className="text-xs text-gray-500">Live estimates</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div className="rounded-lg border border-gray-100 px-3 py-2 flex items-center justify-between">
                <span className="pod-metric-label">Per Hour</span>
                <span className="text-sm font-semibold text-green-600">
                  ${costs.current_cost_per_hour?.toFixed(3) || '0.000'}
                </span>
              </div>
              <div className="rounded-lg border border-gray-100 px-3 py-2 flex items-center justify-between">
                <span className="pod-metric-label">Daily</span>
                <span className="text-sm font-semibold text-blue-600">
                  ${costs.estimated_daily_cost?.toFixed(2) || '0.00'}
                </span>
              </div>
              <div className="rounded-lg border border-gray-100 px-3 py-2 flex items-center justify-between">
                <span className="pod-metric-label">Monthly</span>
                <span className="text-sm font-semibold text-purple-600">
                  ${costs.estimated_monthly_cost?.toFixed(0) || '0'}
                </span>
              </div>
              <div className="rounded-lg border border-gray-100 px-3 py-2 flex items-center justify-between">
                <span className="pod-metric-label">Active Pods</span>
                <span className="text-sm font-semibold text-gray-800">
                  {costs.active_pods || 0}/{costs.total_pods || 0}
                </span>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div className="card pod-card pod-card-compact">
            <div className="flex items-center justify-between text-sm font-semibold text-gray-800">
              <span className="inline-flex items-center gap-2">
                <ListChecks className="w-4 h-4 text-blue-600" />
                Environment Recipes
              </span>
              <button
                className="btn-secondary btn-sm"
                onClick={() => fetchRecipes()}
                disabled={isRecipeLoading}
              >
                {isRecipeLoading ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>
            {recipes.length === 0 ? (
              <p className="text-sm text-gray-600">
                No recipes defined yet. Use the API (`POST /recipes`) to capture your workspace bundles.
              </p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {recipes.slice(0, 4).map((recipe) => (
                  <li key={recipe.id} className="py-1.5 text-sm">
                    <div className="flex items-center justify-between gap-3">
                      <span className="font-medium">{recipe.name}</span>
                      <span className="text-xs text-gray-500 truncate">
                        {(recipe.tags || []).join(', ') || 'untagged'}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {recipe.base_image?.slice(0, 60) || 'Base image n/a'}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="card pod-card pod-card-compact">
            <div className="flex items-center justify-between text-sm font-semibold text-gray-800">
              <span className="inline-flex items-center gap-2">
                <History className="w-4 h-4 text-purple-600" />
                Recent Restore Jobs
              </span>
              <button className="btn-secondary btn-sm" onClick={fetchRestoreJobs}>
                Refresh
              </button>
            </div>
            {restoreJobs.length === 0 ? (
              <p className="text-sm text-gray-600">No restores have been queued yet.</p>
            ) : (
              <ul className="space-y-2 max-h-60 overflow-y-auto">
                {restoreJobs.slice(0, 5).map((job) => (
                  <li key={job.id} className="rounded-md border border-gray-200 p-3 text-sm space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-semibold capitalize">{job.status}</span>
                      <span className="text-xs text-gray-500">
                        {job.updated_at ? new Date(job.updated_at).toLocaleTimeString() : '—'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-xs text-gray-600">
                      <span>Pod {job.pod_id}</span>
                      <span>Recipe {job.recipe_id?.slice(0, 6) || 'n/a'}</span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Pods Grid */}
        <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-semibold">Active Pods</h2>
            <span className="text-sm text-gray-600">
              {pods.length} pod{pods.length !== 1 ? 's' : ''} total
            </span>
          </div>
          {pods.length > 0 && (
            <button
              onClick={() => setShowInlineCreator((prev) => !prev)}
              className="btn-primary btn-sm inline-flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              {showInlineCreator ? 'Hide Creator' : 'Create Pod'}
            </button>
          )}
        </div>

        {pods.length === 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
            <div className="card space-y-3 p-5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <Server className="w-10 h-10 text-gray-400" />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800">No Pods Found</h3>
                  </div>
                </div>
                <button
                  className="btn-primary btn-sm inline-flex items-center gap-2"
                  onClick={() => setShowEmptyStateCreator((prev) => !prev)}
                >
                  <Plus className="w-4 h-4" />
                  {showEmptyStateCreator ? 'Hide Creator' : 'Create Pod'}
                </button>
              </div>
              <p className="text-sm text-gray-600">
                Launch your first pod to start managing ComfyUI environments from this dashboard.
              </p>
              <p className="text-xs text-gray-500">
                Need a guide? Review the template wizard checklist and recipe docs linked in the runbooks before launching production pods.
              </p>
            </div>
            {showEmptyStateCreator && (
              <CreatePodModal
                inline
                isOpen
                onCreatePod={handleCreatePod}
                gpuTypes={gpuTypes}
                onInlineClose={() => setShowEmptyStateCreator(false)}
              />
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {showInlineCreator && (
              <CreatePodModal
                inline
                isOpen
                onCreatePod={handleCreatePod}
                gpuTypes={gpuTypes}
                onInlineClose={() => setShowInlineCreator(false)}
              />
            )}
            {pods.map((pod) => {
              const assignedProfile = backupProfiles.find((item) => item.id === profileBindings[pod.id])
              return (
                <PodComponent
                  key={pod.id}
                  pod={pod}
                  onAction={handlePodAction}
                  isActing={actingPods.has(pod.id)}
                  gpuTypeMap={gpuTypeMap}
                  profiles={backupProfiles}
                  assignedProfileId={profileBindings[pod.id]}
                  onAssignProfile={handleAssignProfile}
                  onBackupNow={handleBackupNow}
                  isBackingUp={backingUpPods.has(pod.id)}
                  defaultCategories={profileDefaults.categories || DEFAULT_AUTOSAVE_CONFIG.categories}
                  categoryOptions={CATEGORY_OPTIONS}
                  selectedCategories={getCategoriesForPod(pod.id)}
                  onToggleCategory={handleCategoryToggle}
                  onResetCategories={handleResetCategories}
                  onApplyCategoriesToProfile={handleApplyCategoriesToProfile}
                  canPersistCategories={Boolean(profileBindings[pod.id] && customCategories[pod.id]?.length)}
                  profileName={assignedProfile?.name}
                  recipeName={resolveRecipeName(pod.id)}
                  onOpenRestore={openRestoreModal}
                  connectionInfo={connectionMap[pod.id]}
                />
              )
            })}
          </div>
        )}
      </main>
    </div>
    </>
  )
}

export default App
